import { useEffect, useMemo, useState } from "react";
import { Chart as ChartJS, BarElement, CategoryScale, LinearScale, Tooltip } from "chart.js";
import { Bar } from "react-chartjs-2";
import { parseChartRows, formatChartNumber } from "../../utils/chartRows";
import { CHART_USER_COLOR, getComparisonColor, isMobileViewport } from "../../utils/chartTheme";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip);

// Draws a circle at the tip of each bar. `bar.tooltipPosition()` returns the
// center of the bar rectangle, which is wrong for a lollipop - the circle
// belongs at the value end, i.e. the bar's own (x, y).
const drawTopCirclesPlugin = {
  id: "drawTopCircles",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const radius = chart.width < 480 ? 8 : 12;

    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const { x, y } = bar;

        ctx.beginPath();
        ctx.arc(x, y, radius, 0, 2 * Math.PI);
        ctx.fillStyle = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor[index] : dataset.backgroundColor;
        ctx.fill();
        ctx.closePath();
      });
    });
  },
};

// Live chart rows are always `{ label, value, is_user, legend_label? }`.
// Row count is not capped - 3, 5, or more points all get their own color.
const LollipopChart = ({ item, yAxisTitle }) => {
  const [isMobile, setIsMobile] = useState(isMobileViewport());

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileViewport());

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const points = useMemo(
    () =>
      parseChartRows(item).map(point => ({
        ...point,
        legendLabel: point.legendLabel || point.label || "",
      })),
    [item],
  );

  const coloredPoints = useMemo(() => {
    let comparisonIndex = 0;

    return points.map(point => {
      if (point.isUser) return { ...point, color: CHART_USER_COLOR };

      const color = getComparisonColor(comparisonIndex);
      comparisonIndex += 1;
      return { ...point, color };
    });
  }, [points]);

  const chartData = useMemo(
    () => ({
      labels: coloredPoints.map(point => point.label),
      datasets: [
        {
          label: "",
          data: coloredPoints.map(point => point.value),
          backgroundColor: coloredPoints.map(point => point.color),
          barThickness: 3,
          borderRadius: 50,
          categoryPercentage: 0.5,
          barPercentage: 0.5,
        },
      ],
    }),
    [coloredPoints],
  );

  if (coloredPoints.length === 0) return null;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    // The visible hit target is a 3px stem plus a circle drawn by a canvas
    // plugin (not a real interactive element), so `intersect: true` (the
    // Chart.js default) would make hovering the tooltip on almost impossible.
    // `index` + `intersect: false` triggers it for the nearest category.
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      tooltip: {
        // Desktop: hover tooltip. Mobile/touch: disabled here in favor of the
        // always-visible list rendered below the chart (touch "hover" has no
        // reliable equivalent, and a persistent list is easier to read).
        enabled: !isMobile,
        displayColors: false,
        backgroundColor: "#fff",
        borderColor: "#F2F4F7",
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        titleColor: "#475467",
        titleFont: { size: 12, weight: "500" },
        bodyColor: "#344054",
        bodyFont: { size: 12, weight: "600" },
        callbacks: {
          title: contextItems => contextItems[0]?.label ?? "",
          label: context => formatChartNumber(context.raw),
        },
      },
    },
    scales: {
      y: {
        // No `max`/`stepSize` override: let Chart.js compute a clean top and
        // ~8-11 readable ticks on its own.
        beginAtZero: true,
        ticks: {
          callback: value => formatChartNumber(value),
          color: "#4B5563",
          font: {
            size: 14,
            weight: "500",
          },
        },
        title: {
          display: true,
          text: yAxisTitle || "Value",
          color: "#1F2937",
          font: {
            size: isMobile ? 12 : 16,
            weight: "bold",
          },
          padding: {
            bottom: 10,
          },
        },
        grid: {
          drawBorder: false,
          color: "#E5E7EB",
        },
      },
      x: {
        // Category names are surfaced via the legend + tooltip/list instead
        // of axis ticks, matching the Figma lollipop layout.
        ticks: { display: false },
        grid: { display: false },
      },
    },
  };

  return (
    <div className="report-lollipop-chart">
      <div className="chart-legend flex flex-align-center flex-justify-center flex-wrap mb-12">
        {coloredPoints.map((point, index) => (
          <p key={index} className="chart-legend__item medium-text text-color-grey">
            <span className="chart-legend__dot" style={{ background: point.color }} />
            {point.legendLabel}
          </p>
        ))}
      </div>
      <div className="report-lollipop-chart__canvas">
        <Bar data={chartData} options={options} plugins={[drawTopCirclesPlugin]} />
      </div>
      {isMobile && (
        <ul className="report-lollipop-chart__value-list">
          {coloredPoints.map((point, index) => (
            <li key={index} className="report-lollipop-chart__value-list-item">
              <span className="chart-legend__dot" style={{ background: point.color }} />
              <span className="report-lollipop-chart__value-list-label">{point.label}</span>
              <span className="report-lollipop-chart__value-list-value">{formatChartNumber(point.value)}</span>
            </li>
          ))}
        </ul>
      )}
      {item?.x_axis_label && <div className="horizontal-value text-center text-color-grey">{item.x_axis_label}</div>}
    </div>
  );
};

export default LollipopChart;
